// Layer 3 (Transformation): pure projections for the Cost & revenue scene —
// no React, no engine mutations. Physical quantity × unit cost stays visible
// at every step (v19 M12 §3 CostLine discipline): every dollar line below
// carries its quantity and rate, not just the product.

import type { SurfacePageTabItem } from '@/design-system';
import type { VizTone } from '@/versions/v1/modules/_viz';
import type { TrendReference, TrendSeries } from '@/versions/v1/modules/_viz/TrendChart';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { capacityAtReference } from '@/versions/v1/shared/CapacityEngine';
import { addMinutes } from '@/versions/v1/shared/clock.derive';
import {
  formatMoney,
  formatMoneyConsistent,
  formatMoneyPerDst,
  nsrRead,
  readingNum,
  recoveryShortfallCost,
} from '@/versions/v1/shared/NsrEngine';
import { INITIAL_STATE } from '@/versions/v1/shared/ScenarioEngine';
import {
  BUDGET_TONNES_PER_DAY,
  COST_DAYS,
  COST_SEED_PROVENANCE,
  GRINDING_MEDIA_UNIT_COST,
  IMPROVEMENT_OPPORTUNITIES,
  LABOUR_UNIT_COST,
  MAINTENANCE_UNIT_COST,
  POWER_UNIT_COST,
  REAGENT_UNIT_COSTS,
  type CostDaySeed,
  type DayConcGrades,
  type DayRecoveries,
  type ImprovementCategory,
  type ImprovementStage,
} from './seed';
import { BUNKER_HILL_SMELTER_SCHEDULE } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import { formatCurrencyExact, CURRENCY_GLYPH } from '@/versions/v1/shared/money';
import { settleAtShortTonFeed } from '@/versions/v1/shared/NsrEngine';
import { juneMtdTonnes } from '@/versions/v1/shared/june-tonnage.derive';
import { periodBalance } from '@/versions/v1/modules/ingestion/periodBalance';

// ---------------------------------------------------------------------------
// Production-day state — D25. A `COST_DAYS` date is a **production day**, whose
// daily reading is stamped 18:00 the evening before (night shift 18:00–06:00 +
// day shift 06:00–18:00). `stateForDay` maps the production day to that stamp,
// so every row's head grades, penalty assays and revenue Budget read the same
// reading the row's tonnage came from. The round-1 live-cursor "reference day"
// is gone: no row mixes a production day's tonnage with the app cursor's assays.
// ---------------------------------------------------------------------------

/** The 18:00-evening-before stamp of a production day — `2026-06-08` → `2026-06-07T18:00`. */
export function stampForProductionDay(productionDate: string): SeriesTime {
  return addMinutes(`${productionDate}T18:00`, -24 * 60);
}

export function stateForDay(productionDate: string): SessionState {
  return { cursor: stampForProductionDay(productionDate), scenarios: [], whatIf: null, cadence: 'lab' };
}

export function budgetRevenueForDay(day: CostDaySeed): number {
  return nsrRead(stateForDay(day.date), day.tonnesMilled).budgetDaily;
}

// ---------------------------------------------------------------------------
// Signed money — `formatMoney` abbreviates from $10K up and leaves every
// smaller figure in full digits, but it branches on `n >= …`, so a negative
// falls straight through to the plain branch: −376,000 reads `$-376,000`
// beside a positive `$376K`, with an ASCII hyphen where the rest of the
// route writes `−`. Every Δ / variance cell on this route is signed, so the
// sign is lifted out and the magnitude formatted by the same helper: the
// positive path is byte-identical to before, the negative path abbreviates.
// ---------------------------------------------------------------------------

export function formatSignedMoney(n: number): string {
  const sign = n >= 0 ? '+' : '−';
  return `${sign}${formatMoney(Math.abs(n))}`;
}

// MF-8: restored — `scripts/seed/nsr-probe-entry.ts` imports this export by
// name and the probe must keep working unchanged. A thin, pure wrapper over
// the shared engine's `recoveryShortfallCost`; not rendered by this surface.
export type RecoveryShortfallPanel = {
  title: string;
  mtdTotal: string;
  metals: readonly { metal: string; mtd: string }[];
  terms: string;
  basisLabel: string;
  provenance: string;
};

export function recoveryShortfallPanel(state: SessionState): RecoveryShortfallPanel {
  const shortfall = recoveryShortfallCost(state);
  return {
    title: 'Recovery shortfall — this month',
    mtdTotal: formatMoney(shortfall.mtdTotal),
    metals: shortfall.metals.map((m) => ({
      metal: m.metal,
      mtd: formatMoneyConsistent(m.mtdCost),
    })),
    terms: shortfall.terms,
    basisLabel: shortfall.basisLabel,
    provenance: shortfall.provenance,
  };
}

// ---------------------------------------------------------------------------
// Shared copy + tabs
// ---------------------------------------------------------------------------

export type CostTab = 'pl' | 'cost-per-tonne' | 'nsr' | 'matrix' | 'improvements' | 'pipeline';

export const COST_TABS: readonly SurfacePageTabItem<CostTab>[] = [
  { id: 'pl', label: 'P&L' },
  { id: 'cost-per-tonne', label: 'Cost per tonne' },
  { id: 'nsr', label: 'NSR & prices' },
  { id: 'matrix', label: 'Priority matrix' },
  { id: 'improvements', label: 'Opportunity register' },
  { id: 'pipeline', label: 'Pipeline' },
];

export function isCostTab(value: string | null): value is CostTab {
  return COST_TABS.some((item) => item.id === value);
}

export type CostCopy = { title: string };

export function costCopy(_plantName: string): CostCopy {
  return { title: 'Cost & revenue' };
}

export type CostPeriod = 'yesterday' | 'week' | 'month';

export const COST_PERIODS: { id: CostPeriod; label: string }[] = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'Week to date' },
  { id: 'month', label: 'Month to date' },
];

export function isCostPeriod(value: string | null): value is CostPeriod {
  return COST_PERIODS.some((item) => item.id === value);
}

// ---------------------------------------------------------------------------
// Revenue by concentrate — mirrors the plant's Pb/Zn/Ag-on-Pb NSR math
// (NsrEngine.ts) over this module's own seeded production days.
// ---------------------------------------------------------------------------

type ProductBreakdown = {
  concDmt: number;
  grade: number;
  gradeUnit: string;
  payableMetal: number;
  payableUnit: string;
  price: number;
  priceUnit: string;
  tcrc: number;
  penalty: number;
  netRevenue: number;
};

type DayRevenue = { pb: ProductBreakdown; zn: ProductBreakdown; ag: ProductBreakdown; total: number };

function revenueForInputs(
  tonnesMilled: number,
  recoveries: DayRecoveries,
  concGrades: DayConcGrades,
  pbInZnConcPct: number,
  state: SessionState,
): DayRevenue {
  const schedule = BUNKER_HILL_SMELTER_SCHEDULE;
  const settlement = settleAtShortTonFeed({
    feedShortTonsPerDay: tonnesMilled,
    headGrades: {
      leadFraction: readingNum('rd-head-pb', state) / 100,
      zincFraction: readingNum('rd-head-zn', state) / 100,
      silverOzPerShortTon: readingNum('rd-head-ag', state),
    },
    recoveries: { leadPct: recoveries.pb, zincPct: recoveries.zn, silverPct: recoveries.ag },
    concentrateGrades: { leadPct: concGrades.pb, zincPct: concGrades.zn },
    assays: {
      leadAsPct: readingNum('rd-spec-pb-as', state),
      leadHgPpm: readingNum('rd-spec-pb-hg', state),
      zincAsPct: readingNum('rd-spec-zn-as', state),
      zincPbPct: pbInZnConcPct,
    },
  });
  const row = (
    read: typeof settlement.lead | typeof settlement.zinc | typeof settlement.silver,
    grade: number,
    gradeUnit: string,
    payableUnit: string,
    price: number,
    priceUnit: string,
    concentrateDmt: number,
  ): ProductBreakdown => ({
    concDmt: concentrateDmt,
    grade,
    gradeUnit,
    payableMetal: read.payableMetalPerDay,
    payableUnit,
    price,
    priceUnit,
    tcrc: read.treatmentChargeA + read.transportA,
    penalty: read.penaltiesA,
    netRevenue: read.netNsrA,
  });

  return {
    pb: row(settlement.lead, concGrades.pb, '% Pb', 't', schedule.prices.pbPerTonne, `${CURRENCY_GLYPH}/dmt`, settlement.lead.concentrateDmtPerDay),
    zn: row(settlement.zinc, concGrades.zn, '% Zn', 't', schedule.prices.znPerTonne, `${CURRENCY_GLYPH}/dmt`, settlement.zinc.concentrateDmtPerDay),
    ag: row(settlement.silver, concGrades.ag, 'oz/t Ag', 'oz', schedule.prices.agPerOz, `${CURRENCY_GLYPH}/oz`, settlement.lead.concentrateDmtPerDay),
    total: settlement.totalNsrA,
  };
}

function revenueForDay(day: CostDaySeed): DayRevenue {
  return revenueForInputs(day.tonnesMilled, day.recoveries, day.concGrades, day.pbInZnConcPct, stateForDay(day.date));
}

// ---------------------------------------------------------------------------
// Cost build-up — physical quantity × unit cost, category subtotals.
// ---------------------------------------------------------------------------

export type CostLine = {
  label: string;
  qty: number;
  qtyUnit: string;
  rate: number;
  rateUnit: string;
  dollars: number;
};

/** SF-1: dollars ÷ tonnes milled for the period — attached once the period total is known (costPeriod). */
export type CostLineView = CostLine & { dollarsPerT: number };

export type CostCategoryName = 'Reagents' | 'Power' | 'Grinding media' | 'Labour' | 'Maintenance';

export type CostCategory = {
  category: CostCategoryName;
  lines: readonly CostLineView[];
  subtotal: number;
  subtotalPerT: number;
};

function reagentLines(tonnesMilled: number): CostLine[] {
  return REAGENT_UNIT_COSTS.map((r) => {
    const qtyKg = (r.doseGramsPerTonne * tonnesMilled) / 1000;
    return {
      label: r.name,
      qty: qtyKg,
      qtyUnit: 'kg',
      rate: r.ratePerKg,
      rateUnit: `${CURRENCY_GLYPH}/kg`,
      dollars: qtyKg * r.ratePerKg,
    };
  });
}

function mediaLines(tonnesMilled: number): CostLine[] {
  const qtyT = (GRINDING_MEDIA_UNIT_COST.kgPerTonne * tonnesMilled) / 1000;
  return [
    {
      label: 'Steel media',
      qty: qtyT,
      qtyUnit: 't',
      rate: GRINDING_MEDIA_UNIT_COST.ratePerTonne,
      rateUnit: `${CURRENCY_GLYPH}/t`,
      dollars: qtyT * GRINDING_MEDIA_UNIT_COST.ratePerTonne,
    },
  ];
}

function powerLines(tonnesMilled: number): CostLine[] {
  const bopKwh = POWER_UNIT_COST.balanceOfPlantKwhPerTonne * tonnesMilled;
  return [
    {
      label: 'BM01 ball mill',
      qty: POWER_UNIT_COST.bm01KwhPerDay,
      qtyUnit: 'kWh',
      rate: POWER_UNIT_COST.ratePerKwh,
      rateUnit: `${CURRENCY_GLYPH}/kWh`,
      dollars: POWER_UNIT_COST.bm01KwhPerDay * POWER_UNIT_COST.ratePerKwh,
    },
    {
      label: 'Balance of plant',
      qty: bopKwh,
      qtyUnit: 'kWh',
      rate: POWER_UNIT_COST.ratePerKwh,
      rateUnit: `${CURRENCY_GLYPH}/kWh`,
      dollars: bopKwh * POWER_UNIT_COST.ratePerKwh,
    },
  ];
}

function labourLines(): CostLine[] {
  return [
    {
      label: 'Day + night shift',
      qty: LABOUR_UNIT_COST.shiftsPerDay,
      qtyUnit: 'shifts',
      rate: LABOUR_UNIT_COST.ratePerShift,
      rateUnit: `${CURRENCY_GLYPH}/shift`,
      dollars: LABOUR_UNIT_COST.shiftsPerDay * LABOUR_UNIT_COST.ratePerShift,
    },
  ];
}

// MF-4: two fixed lines every day (one qty always 0), not one line whose
// label switches — so `mergeLineSets` (which merges by array position) sums
// qty and dollars at a single rate per line instead of conflating two rates
// under whichever day happened to come first in the period.
function maintenanceLines(date: string | null): CostLine[] {
  const bumped = date !== null && MAINTENANCE_UNIT_COST.bumpDates.has(date);
  return [
    {
      label: 'Consumables',
      qty: bumped ? 0 : 1,
      qtyUnit: 'day',
      rate: MAINTENANCE_UNIT_COST.ratePerDay,
      rateUnit: `${CURRENCY_GLYPH}/day`,
      dollars: bumped ? 0 : MAINTENANCE_UNIT_COST.ratePerDay,
    },
    {
      label: 'Consumables — WO-441 parts',
      qty: bumped ? 1 : 0,
      qtyUnit: 'day',
      rate: MAINTENANCE_UNIT_COST.bumpRatePerDay,
      rateUnit: `${CURRENCY_GLYPH}/day`,
      dollars: bumped ? MAINTENANCE_UNIT_COST.bumpRatePerDay : 0,
    },
  ];
}

function sumLines(lines: readonly CostLine[]): number {
  return lines.reduce((sum, l) => sum + l.dollars, 0);
}

function mergeLineSets(dayLineSets: readonly CostLine[][]): CostLine[] {
  const first = dayLineSets[0] ?? [];
  const merged = first.map((line, idx) => {
    const qty = dayLineSets.reduce((sum, set) => sum + (set[idx]?.qty ?? 0), 0);
    const dollars = dayLineSets.reduce((sum, set) => sum + (set[idx]?.dollars ?? 0), 0);
    return { ...line, qty, dollars };
  });
  // MF-4 discipline: qty × rate must equal dollars for every merged line
  // (every day contributing to a line shares that line's rate — enforced by
  // maintenanceLines emitting one fixed-rate line per rate, never a label
  // that switches rate mid-period).
  for (const line of merged) {
    const expected = Math.round(line.qty * line.rate * 100) / 100;
    const actual = Math.round(line.dollars * 100) / 100;
    if (Math.abs(expected - actual) > 0.5) {
      throw new Error(`cost line discipline broken: ${line.label} — qty ${line.qty} × rate ${line.rate} = ${expected}, dollars ${actual}`);
    }
  }
  // Drop a line whose qty is zero across the whole period (MF-4) — e.g. no
  // WO-441 day inside Yesterday, or no non-bumped Consumables day inside a
  // maintenance-only slice.
  return merged.filter((line) => line.qty !== 0);
}

function withPerTonne(lines: readonly CostLine[], tonnesMilled: number): CostLineView[] {
  return lines.map((line) => ({ ...line, dollarsPerT: tonnesMilled > 0 ? line.dollars / tonnesMilled : 0 }));
}

export function costCategoriesForDays(days: readonly CostDaySeed[], tonnesMilled: number): CostCategory[] {
  const reagents = mergeLineSets(days.map((d) => reagentLines(d.tonnesMilled)));
  const media = mergeLineSets(days.map((d) => mediaLines(d.tonnesMilled)));
  const power = mergeLineSets(days.map((d) => powerLines(d.tonnesMilled)));
  const labour = mergeLineSets(days.map(() => labourLines()));
  const maintenance = mergeLineSets(days.map((d) => maintenanceLines(d.date)));

  const perT = (dollars: number) => (tonnesMilled > 0 ? dollars / tonnesMilled : 0);

  return [
    { category: 'Reagents', lines: withPerTonne(reagents, tonnesMilled), subtotal: sumLines(reagents), subtotalPerT: perT(sumLines(reagents)) },
    { category: 'Power', lines: withPerTonne(power, tonnesMilled), subtotal: sumLines(power), subtotalPerT: perT(sumLines(power)) },
    { category: 'Grinding media', lines: withPerTonne(media, tonnesMilled), subtotal: sumLines(media), subtotalPerT: perT(sumLines(media)) },
    { category: 'Labour', lines: withPerTonne(labour, tonnesMilled), subtotal: sumLines(labour), subtotalPerT: perT(sumLines(labour)) },
    { category: 'Maintenance', lines: withPerTonne(maintenance, tonnesMilled), subtotal: sumLines(maintenance), subtotalPerT: perT(sumLines(maintenance)) },
  ];
}

export function totalCostForDays(days: readonly CostDaySeed[]): number {
  return costCategoriesForDays(days, 0).reduce((sum, c) => sum + c.subtotal, 0);
}

/** The Budget basis, category by category: the same five line builders at
 *  `BUDGET_TONNES_PER_DAY` for `dayCount` days, with maintenance at its
 *  non-bumped rate. The P&L's per-category Budget columns, the cost-per-tonne
 *  variance cards and `budgetCostForDays` all read this one array, so a
 *  category cannot drift from the total it sums into.
 *
 *  The two count-like differences from `costCategoriesForDays` are deliberate
 *  and not equivalent: `maintenanceLines(null)` is the un-bumped day, so the
 *  WO-441 parts line is present with qty 0 rather than dropped, and no tonnes
 *  are passed, so `dollarsPerT` is 0 — the Budget view is a level, not a
 *  period-scaled quantity. */
export function budgetCostCategoriesForDays(dayCount: number): CostCategory[] {
  const scale = (lines: readonly CostLine[]) =>
    lines.map((line) => ({ ...line, qty: line.qty * dayCount, dollars: line.dollars * dayCount }));
  const build = (category: CostCategory['category'], lines: CostLine[]): CostCategory => {
    const scaled = scale(lines);
    return { category, lines: withPerTonne(scaled, 0), subtotal: sumLines(scaled), subtotalPerT: 0 };
  };

  return [
    build('Reagents', reagentLines(BUDGET_TONNES_PER_DAY)),
    build('Power', powerLines(BUDGET_TONNES_PER_DAY)),
    build('Grinding media', mediaLines(BUDGET_TONNES_PER_DAY)),
    build('Labour', labourLines()),
    build('Maintenance', maintenanceLines(null)),
  ];
}

export function budgetCostForDays(dayCount: number): number {
  return budgetCostCategoriesForDays(dayCount).reduce((sum, c) => sum + c.subtotal, 0);
}

// ---------------------------------------------------------------------------
// Period selection + headline
// ---------------------------------------------------------------------------

export function daysForPeriod(period: CostPeriod): readonly CostDaySeed[] {
  if (period === 'yesterday') return COST_DAYS.slice(-1); // 07 Jun — the last complete production day
  if (period === 'week') return COST_DAYS.slice(1); // 02–07 Jun — the complete days inside the 02–08 Jun production week
  return COST_DAYS; // 01–07 Jun month to date (D25 complete production days)
}

export type HeadlineFigure = {
  actual: number;
  budget: number;
  deltaPct: number;
};

export type CostHeadline = {
  netRevenue: HeadlineFigure;
  operatingCost: HeadlineFigure;
  margin: HeadlineFigure;
  marginPerTonne: HeadlineFigure;
};

/** `RevenueRow` carries the money twice: the display strings the table prints,
 *  and the raw terms behind them. `netRevenue` is the one panel on this route
 *  whose arithmetic nothing else checks, so the four terms are kept reachable —
 *  `netRevenueRaw === payableMetalRaw × priceRaw − tcrcRaw − penaltyRaw` is a
 *  real assertion (probe P6) rather than a restatement of the formatted cells.
 *  `priceRaw` is a price, not a quantity, so it is the period's single schedule
 *  price and is not summed across the row's days. */
export type RevenueRow = {
  product: 'Pb concentrate' | 'Zn concentrate' | 'Ag payable';
  concentrateTonnes: string;
  grade: string;
  payableMetal: string;
  price: string;
  tcrc: string;
  penalty: string;
  penaltyRaw: number;
  netRevenue: string;
  payableMetalRaw: number;
  priceRaw: number;
  tcrcRaw: number;
  netRevenueRaw: number;
};

/** The same three products' money unformatted, so a projection that needs to
 *  do arithmetic on revenue reads numbers rather than re-parsing the display
 *  strings above. Same figures, one source. */
export type RevenueByProduct = {
  pb: number;
  zn: number;
  ag: number;
  total: number;
};

export type CostPeriodView = {
  period: CostPeriod;
  label: string;
  /** Date range only — SF-3, no generic "Headline" title, no narration verbs. */
  dateRange: string;
  /** Set only when another route's same-named period covers a wider window
   *  (MF-6): the in-progress day at the demo cursor, named once so the
   *  figures never silently disagree with Metal accounting's MTD/WTD. */
  windowNote: string | null;
  tonnesMilled: number;
  headline: CostHeadline;
  costCategories: readonly CostCategory[];
  costTotal: number;
  /** Reagent spend is the extra tonnage at the budget rate, not a rate
   *  overrun, when the actual reagent cost per short ton equals the budget
   *  rate per short ton. One test feeds the P&L row tone, the build-up row and
   *  the cost-per-tonne card, so the reagent variance carries one tone on the
   *  route — neutral, the volume-driven framing the landing's risk card uses. */
  reagentVarianceVolumeDriven: boolean;
  revenueRows: readonly RevenueRow[];
  revenueByProduct: RevenueByProduct;
  revenueTotal: number;
};

function fmtT(n: number): string {
  return `${n.toFixed(1)} dmt`;
}

function fmtOz(n: number): string {
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} oz`;
}

/** The Ag payable grade the revenue table prints — Metal accounting's own read
 *  for its windows. `/cost`'s month and week windows are the balance's MTD and
 *  WTD windows exactly (1–7 Jun and 2–7 Jun, D25), so this reads
 *  `periodBalance(...).productGrade.agConcOzPerT`, which F765 (#765) sets to the
 *  **assayed** basis: the day's SA03 assay and, over WTD/MTD, the daily
 *  `rs-ag-grade` assay mass-weighted by each production day's Pb/Ag concentrate
 *  dmt (12.65 oz/t month to date, 12.69 week). #729 had read the balance's
 *  back-calculated product grade (11.24 / 11.41), which never saw K20's re-base
 *  and contradicted the day's 12.14; that figure now survives only inside the
 *  closure (`closureAgConcOzPerT`). `Yesterday` is one complete production day
 *  with no balance window of its own, so it reads the same single-day identity
 *  over that day's seed rows (feed × head Ag × recovery ÷ the day's Pb/Ag
 *  concentrate mass) — the day's own balance, unchanged from #729. Falls back to
 *  the series read only when the balance is unmeasured (Metal accounting renders
 *  those periods unmeasured). */
function massWeightedAgGrade(period: CostPeriod, days: readonly CostDaySeed[]): number | null {
  if (period === 'month' || period === 'week') {
    const balance = periodBalance(period === 'month' ? 'mtd' : 'wtd');
    return balance ? balance.productGrade.agConcOzPerT : null;
  }
  const day = days[days.length - 1];
  if (!day) return null;
  const state = stateForDay(day.date);
  const containedAgOz = day.tonnesMilled * readingNum('rd-head-ag', state) * (day.recoveries.ag / 100);
  const leadConcDst =
    (day.tonnesMilled * (readingNum('rd-head-pb', state) / 100) * (day.recoveries.pb / 100)) /
    (day.concGrades.pb / 100);
  return leadConcDst > 0 ? containedAgOz / leadConcDst : null;
}

export function costPeriod(period: CostPeriod): CostPeriodView {
  const days = daysForPeriod(period);
  // The ore tonnage is the one June-to-date function's read (D25: the complete
  // production days 1–7 Jun at the demo cursor), never a second sum of the
  // seed's per-day quantities. The per-day quantities below stay the build-up's
  // physical quantities; their sum equals this read (the probe ties them).
  const tonnesMilled =
    period === 'month'
      ? juneMtdTonnes(INITIAL_STATE).tonnesDst
      : days.reduce((sum, d) => sum + d.tonnesMilled, 0);

  const revenues = days.map(revenueForDay);
  const budgetRevenue = days.reduce((sum, d) => sum + budgetRevenueForDay(d), 0);

  const costTotal = totalCostForDays(days);
  const budgetCost = budgetCostForDays(days.length);
  const costCategories = costCategoriesForDays(days, tonnesMilled);
  // Reagent rate test: actual cost per short ton against the budget rate per
  // short ton, to the cent the glass prints. When equal, every US$ of the
  // variance is extra tonnage, not a rate overrun.
  const budgetTonnes = days.length * BUDGET_TONNES_PER_DAY;
  const reagentActual = costCategories.find((c) => c.category === 'Reagents')?.subtotal ?? 0;
  const reagentBudget = budgetCostCategoriesForDays(days.length).find((c) => c.category === 'Reagents')?.subtotal ?? 0;
  const reagentVarianceVolumeDriven =
    tonnesMilled > 0 && budgetTonnes > 0 && Math.abs(reagentActual / tonnesMilled - reagentBudget / budgetTonnes) < 0.005;

  const pbTotal = revenues.reduce((s, r) => s + r.pb.netRevenue, 0);
  const znTotal = revenues.reduce((s, r) => s + r.zn.netRevenue, 0);
  const agTotal = revenues.reduce((s, r) => s + r.ag.netRevenue, 0);
  // The total is the sum of the three product nets, not a second sum of the
  // per-day totals: the daily total is a fourth accumulation of the same three
  // figures, and floating-point addition is not associative, so summing it
  // would leave the total a last bit apart from the parts printed on the same
  // row. `revenueForDay().total` stays for the daily-margin chart, where only
  // that one day's figure matters.
  const netRevenue = pbTotal + znTotal + agTotal;

  const margin = netRevenue - costTotal;
  const budgetMargin = budgetRevenue - budgetCost;
  const marginPerTonne = tonnesMilled > 0 ? margin / tonnesMilled : 0;
  // Revenue Budget is basis actual tonnes (MF-6); the margin/t Budget
  // comparator divides by the same actual tonnage, not a separate flat-ramp
  // tonnage, so the two Budget figures share one denominator.
  const budgetMarginPerTonneForPeriod = tonnesMilled > 0 ? budgetMargin / tonnesMilled : 0;

  const pctDelta = (actual: number, budget: number) => (budget !== 0 ? ((actual - budget) / Math.abs(budget)) * 100 : 0);

  const pbConcDmt = revenues.reduce((s, r) => s + r.pb.concDmt, 0);
  const znConcDmt = revenues.reduce((s, r) => s + r.zn.concDmt, 0);
  const pbPayable = revenues.reduce((s, r) => s + r.pb.payableMetal, 0);
  const znPayable = revenues.reduce((s, r) => s + r.zn.payableMetal, 0);
  const agPayable = revenues.reduce((s, r) => s + r.ag.payableMetal, 0);
  const pbTcrc = revenues.reduce((s, r) => s + r.pb.tcrc, 0);
  const znTcrc = revenues.reduce((s, r) => s + r.zn.tcrc, 0);
  const znPenalty = revenues.reduce((s, r) => s + r.zn.penalty, 0);
  const weightedGrade = (metal: 'pb' | 'zn' | 'ag') => {
    const totalDmt = revenues.reduce((s, r) => s + r[metal].concDmt, 0);
    if (totalDmt === 0) return 0;
    return revenues.reduce((s, r) => s + r[metal].concDmt * r[metal].grade, 0) / totalDmt;
  };
  // Issue #765: the Ag grade is the balance's reported (assayed) product grade,
  // one derivation shared with Metal accounting's MTD/WTD windows. The series
  // read survives only as the unmeasured-balance fallback.
  const agGrade = massWeightedAgGrade(period, days) ?? weightedGrade('ag');

  const revenueRows: RevenueRow[] = [
    {
      product: 'Pb concentrate',
      concentrateTonnes: fmtT(pbConcDmt),
      grade: `${weightedGrade('pb').toFixed(1)}% Pb`,
      payableMetal: `${pbPayable.toFixed(1)} t`,
      price: `${formatCurrencyExact(BUNKER_HILL_SMELTER_SCHEDULE.prices.pbPerTonne)}/dmt`,
      tcrc: formatMoney(pbTcrc),
      penalty: '—',
      penaltyRaw: 0,
      netRevenue: formatMoney(pbTotal),
      payableMetalRaw: pbPayable,
      priceRaw: BUNKER_HILL_SMELTER_SCHEDULE.prices.pbPerTonne,
      tcrcRaw: pbTcrc,
      netRevenueRaw: pbTotal,
    },
    {
      product: 'Zn concentrate',
      concentrateTonnes: fmtT(znConcDmt),
      grade: `${weightedGrade('zn').toFixed(1)}% Zn`,
      payableMetal: `${znPayable.toFixed(1)} t`,
      price: `${formatCurrencyExact(BUNKER_HILL_SMELTER_SCHEDULE.prices.znPerTonne)}/dmt`,
      tcrc: formatMoney(znTcrc),
      penalty: znPenalty > 0 ? formatMoney(znPenalty) : '—',
      penaltyRaw: znPenalty,
      netRevenue: formatMoney(znTotal),
      payableMetalRaw: znPayable,
      priceRaw: BUNKER_HILL_SMELTER_SCHEDULE.prices.znPerTonne,
      tcrcRaw: znTcrc,
      netRevenueRaw: znTotal,
    },
    {
      product: 'Ag payable',
      // SF-4: Ag rides the Pb concentrate (AG_TERMS deducts against Pb's
      // dmt) — showing that same dmt figure again reads as a second,
      // separate tonnage, so name the basis instead of repeating the number.
      concentrateTonnes: 'in Pb conc',
      // Issue #765: the Ag grade is the balance's reported (assayed) product
      // grade Metal accounting's MTD/WTD strip and table print — 12.65 oz/t
      // month to date, 12.69 week — not the back-calculation #729 had pointed
      // them at (11.24 / 11.41). Prints at 2 dp like every other live Ag figure.
      grade: `${agGrade.toFixed(2)} oz/t`,
      payableMetal: fmtOz(agPayable),
      // Ag prices print two decimals everywhere on the tab (the Metal prices table's policy).
      price: `${formatCurrencyExact(BUNKER_HILL_SMELTER_SCHEDULE.prices.agPerOz)}/oz`,
      tcrc: '—',
      penalty: '—',
      penaltyRaw: 0,
      netRevenue: formatMoney(agTotal),
      payableMetalRaw: agPayable,
      priceRaw: BUNKER_HILL_SMELTER_SCHEDULE.prices.agPerOz,
      tcrcRaw: 0,
      netRevenueRaw: agTotal,
    },
  ];

  const label = COST_PERIODS.find((p) => p.id === period)?.label ?? period;
  const dateRange =
    period === 'yesterday' ? '7 Jun' : period === 'week' ? '2–7 Jun · 6 complete days' : '1–7 Jun · 7 complete days';
  // The window above names the period; comparing it with another route's window
  // on the glass is narration.
  const windowNote: string | null = null;

  return {
    period,
    label,
    dateRange,
    windowNote,
    tonnesMilled,
    headline: {
      netRevenue: { actual: netRevenue, budget: budgetRevenue, deltaPct: pctDelta(netRevenue, budgetRevenue) },
      operatingCost: { actual: costTotal, budget: budgetCost, deltaPct: pctDelta(costTotal, budgetCost) },
      margin: { actual: margin, budget: budgetMargin, deltaPct: pctDelta(margin, budgetMargin) },
      marginPerTonne: {
        actual: marginPerTonne,
        budget: budgetMarginPerTonneForPeriod,
        deltaPct: pctDelta(marginPerTonne, budgetMarginPerTonneForPeriod),
      },
    },
    costCategories,
    costTotal,
    reagentVarianceVolumeDriven,
    revenueRows,
    revenueByProduct: { pb: pbTotal, zn: znTotal, ag: agTotal, total: netRevenue },
    revenueTotal: netRevenue,
  };
}

/** One headline figure: label, formatted value, its comparison and the tone
 *  its state earns. The P&L headline carries its seed provenance; the other
 *  card sets (cost per tonne, NSR, matrix) carry none. */
export type CostKpiCard = {
  label: string;
  value: string;
  sub: string;
  tone: VizTone;
  provenance?: string;
};

export function costHeadlineCards(view: CostPeriodView): CostKpiCard[] {
  const { headline } = view;
  // The comparator each figure is read against is named on the caption. Cost's
  // revenue and margin budgets are the design-recovery figure (actual tonnes ×
  // design recovery); the route names that **design potential**, never a second
  // Budget — the GM landing's Budget is the M5 target. Only the operating-cost
  // budget is the M5 Budget. A design-potential gap is not a budget miss, so
  // those three tiles carry the neutral tone the landing gives its
  // design-potential caption rather than a red miss.
  const deltaSub = (f: HeadlineFigure, unit: (n: number) => string, comparator: 'Budget' | 'design potential') =>
    `${f.deltaPct >= 0 ? '+' : '−'}${Math.abs(f.deltaPct).toFixed(0)}% vs ${comparator} ${unit(f.budget)}`;

  return [
    {
      label: 'Net revenue',
      value: formatMoney(headline.netRevenue.actual),
      sub: deltaSub(headline.netRevenue, formatMoney, 'design potential'),
      tone: 'neutral',
      provenance: COST_SEED_PROVENANCE,
    },
    {
      label: 'Operating cost',
      value: formatMoney(headline.operatingCost.actual),
      sub: deltaSub(headline.operatingCost, formatMoney, 'Budget'),
      tone: headline.operatingCost.deltaPct <= 0 ? 'ok' : 'warn',
      provenance: COST_SEED_PROVENANCE,
    },
    {
      label: 'Margin',
      value: formatMoney(headline.margin.actual),
      sub: deltaSub(headline.margin, formatMoney, 'design potential'),
      tone: 'neutral',
      provenance: COST_SEED_PROVENANCE,
    },
    {
      label: 'Margin / dst',
      value: formatMoneyPerDst(headline.marginPerTonne.actual),
      sub: deltaSub(headline.marginPerTonne, formatMoneyPerDst, 'design potential'),
      tone: 'neutral',
      provenance: COST_SEED_PROVENANCE,
    },
  ];
}

// ---------------------------------------------------------------------------
// Daily margin bars
// ---------------------------------------------------------------------------

export type DailyMarginPanel = {
  categories: readonly string[];
  series: readonly TrendSeries[];
  references: readonly TrendReference[];
};

export function dailyMargin(): DailyMarginPanel {
  const dailyBudgetCost = budgetCostForDays(1);
  // The design-recovery revenue varies by day (actual tonnes × design
  // recoveries, MF-6) — the single reference line is the 7-day average
  // design-potential margin (design revenue − budget cost), not one flat
  // figure. It is a design comparator, so it is named **design potential** and
  // carries the neutral tone, never a second Budget on this route.
  const avgDesignMarginK =
    COST_DAYS.reduce((sum, day) => sum + (budgetRevenueForDay(day) - dailyBudgetCost), 0) / COST_DAYS.length / 1000;

  const points = COST_DAYS.map((day) => {
    const revenue = revenueForDay(day).total;
    const cost = totalCostForDays([day]);
    return (revenue - cost) / 1000;
  });

  return {
    categories: COST_DAYS.map((d) => d.date.slice(-2)),
    // Neutral bars: every day's margin is positive and none is a state; the
    // one comparator is the dashed design-potential reference (polish.P7).
    series: [{ name: 'Margin', points, tone: 'neutral' }],
    // A short in-plot label that does not draw over the bars (review MF-2).
    references: [{ value: avgDesignMarginK, label: 'design potential', tone: 'neutral' }],
  };
}

// ---------------------------------------------------------------------------
// Improvement opportunities
// ---------------------------------------------------------------------------

export type ImprovementRow = {
  id: string;
  opportunity: string;
  category: ImprovementCategory;
  evidence: string;
  benefitPerYear: string;
  benefitPerYearRaw: number;
  capex: string;
  paybackYears: string;
  paybackRaw: number;
  stage: ImprovementStage;
  benefitTerms: readonly { label: string; amount: string; positive: boolean }[];
  capexRaw: number;
  capacityCase?: {
    annualBenefitRaw: number;
    paybackMonths: number;
    capexRaw: number;
    evidence: string;
    rows: readonly { label: string; value: string }[];
  };
  exploitSubordinateElevate: { tried: boolean; note: string };
};

function formatDollars(value: number): string {
  const rounded = Math.round(Math.abs(value));
  return `${value < 0 ? '−' : ''}${CURRENCY_GLYPH}${rounded.toLocaleString('en-AU')}`;
}

function formatDollarsCompact(value: number): string {
  const magnitude = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  if (magnitude >= 1_000_000) {
    const amount = (magnitude / 1_000_000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    return `${sign}${CURRENCY_GLYPH}${amount}M`;
  }
  if (magnitude >= 1_000) {
    const amount = (magnitude / 1_000).toFixed(magnitude >= 100_000 ? 0 : 1).replace(/\.0$/, '');
    return `${sign}${CURRENCY_GLYPH}${amount}K`;
  }
  return formatDollars(value);
}

function formatSignedDollarsPerDay(value: number): string {
  return `${value >= 0 ? '+' : '−'}${formatDollars(Math.abs(value))}/day`;
}

const STAGE_TONE: Record<ImprovementStage, VizTone> = {
  candidate: 'neutral',
  priced: 'info',
  'capital request': 'warn',
  approved: 'ok',
};

export function improvementStageTone(stage: ImprovementStage): VizTone {
  return STAGE_TONE[stage];
}

// MF-5: a payback under a year renders in weeks or months, never `0.0 yr`
// (which reads as zero or free).
function formatPayback(years: number): string {
  if (!Number.isFinite(years)) return '—';
  if (years < 1) {
    const months = years * 12;
    if (months < 1) {
      const weeks = Math.max(1, Math.round(years * 52));
      return `${weeks} wk`;
    }
    return `${months.toFixed(1)} mo`;
  }
  return `${years.toFixed(1)} yr`;
}

function capacityCaseRows(read: ReturnType<typeof capacityAtReference>): ImprovementRow['capacityCase'] {
  const currentEbitdaA = read.bottleneckLadder.currentEbitdaA;
  const maximum = read.scenarios.find((scenario) => scenario.id === 'maximum')!;
  const primary = read.bottleneckLadder.rungs.find((rung) => rung.currentBottleneck)!;
  const next = read.bottleneckLadder.rungs.find((rung) => rung.ceilingStph > primary.ceilingStph);
  const annualBenefitRaw = read.step2.potential.annualisedEbitdaGainA;
  const paybackMonths = read.step2.paybackMonths;
  return {
    annualBenefitRaw,
    paybackMonths,
    capexRaw: read.step2.capexA,
    evidence: next
      ? `${primary.name} at ${primary.ceilingStph.toFixed(0)} stph binds before ${next.name} at ${next.ceilingStph.toFixed(0)} stph.`
      : `${primary.name} at ${primary.ceilingStph.toFixed(0)} stph is the current circuit ceiling.`,
    rows: [
      {
        label: 'Primary constraint',
        value: `${primary.name} · ${primary.ceilingStph.toFixed(0)} stph ceiling · ${primary.headroomFromCurrentStph >= 0 ? '+' : '−'}${Math.abs(primary.headroomFromCurrentStph).toFixed(0)} stph vs current ${read.currentThroughputStph.toFixed(0)}`,
      },
      {
        label: 'Step 1 EBITDA',
        value: `${formatSignedDollarsPerDay(read.step1.ebitdaGainA)} vs today's ${formatDollars(currentEbitdaA)}`,
      },
      {
        label: 'Step 2 potential',
        value: `${formatSignedDollarsPerDay(read.step2.potential.ebitdaGainA)} vs ${formatDollars(maximum.financials.ebitdaA)} at Step 1`,
      },
      {
        label: 'Step 2 without capital',
        value: `${formatSignedDollarsPerDay(read.step2.withoutCapital.ebitdaGainA)} vs ${formatDollars(maximum.financials.ebitdaA)} at Step 1`,
      },
      {
        label: 'Recovery-floor cost',
        value: `${formatDollars(read.step2.recoveryFloorCostA)}/day · potential less no-capital Step 2`,
      },
      {
        label: 'Annual benefit',
        value: `${formatDollarsCompact(annualBenefitRaw)}/yr · Step 2 potential annualised`,
      },
      {
        label: 'Payback',
        value: `${formatPayback(paybackMonths / 12)} on ${formatDollarsCompact(read.step2.capexA)} capex at the annual benefit above`,
      },
    ],
  };
}

export function improvementRegister(): { rows: readonly ImprovementRow[] } {
  const referenceCapacity = capacityAtReference();
  const io10Case = capacityCaseRows(referenceCapacity);
  const rows = IMPROVEMENT_OPPORTUNITIES.map((row) => {
    const capacityCase = row.id === 'IO-10' ? io10Case : undefined;
    const benefitTermsSeed = capacityCase
      ? [{ label: 'Step 2 potential annualised', amountPerYear: capacityCase.annualBenefitRaw }]
      : row.benefitTerms;
    const netBenefit = benefitTermsSeed.reduce((s, t) => s + t.amountPerYear, 0);
    const paybackRaw = capacityCase
      ? capacityCase.paybackMonths / 12
      : netBenefit > 0
        ? (row.capex ?? 0) / netBenefit
        : Number.POSITIVE_INFINITY;
    // An unpriced row has no benefit term yet: it prints `—`, never `$0`, so
    // a candidate never reads as an opportunity worth nothing.
    const priced = benefitTermsSeed.length > 0;
    return {
      id: row.id,
      opportunity: row.opportunity,
      category: row.category,
      evidence: capacityCase?.evidence ?? row.evidence,
      benefitPerYear: priced
        ? capacityCase
          ? formatDollarsCompact(netBenefit)
          : formatMoney(netBenefit)
        : '—',
      benefitPerYearRaw: netBenefit,
      capex: priced
        ? capacityCase
          ? formatDollarsCompact(capacityCase.capexRaw)
          : formatMoney(row.capex ?? 0)
        : '—',
      capexRaw: capacityCase?.capexRaw ?? row.capex ?? 0,
      paybackYears: formatPayback(paybackRaw),
      paybackRaw,
      stage: row.stage,
      benefitTerms: benefitTermsSeed.map((t) => ({
        label: t.label,
        amount: `${t.amountPerYear >= 0 ? '+' : '−'}${capacityCase
          ? formatDollarsCompact(Math.abs(t.amountPerYear))
          : formatMoney(Math.abs(t.amountPerYear))}/yr`,
        positive: t.amountPerYear >= 0,
      })),
      capacityCase,
      exploitSubordinateElevate: row.exploitSubordinateElevate,
    };
  });

  return {
    rows: [...rows].sort((a, b) => a.paybackRaw - b.paybackRaw),
  };
}
